import React from 'react';
import { Link } from 'react-router-dom';
import './LegalPage.css';

const SECTIONS = [
  {
    title: 'Acceptance of Terms',
    content: [
      'By downloading, installing, or using DEEP Health — Earn & Track ("the App"), you agree to be bound by these Terms & Conditions ("Terms").',
      'If you do not agree with any part of these Terms, you must not use the App.',
      'These Terms apply to all users who access or use the App in any way.',
      'We reserve the right to update these Terms at any time. We will notify you of material changes via an in-app notification. Continued use after changes are posted constitutes your acceptance.',
    ]
  },
  {
    title: 'DEEP Points — No Monetary Value',
    content: [
      'DEEP Points are a virtual in-app reward system. They have absolutely no monetary value, no guaranteed cash value, and no promise of conversion to any real-world currency or financial asset of any kind.',
      'Accumulating DEEP Points does not entitle you to any payment, compensation, withdrawal, or financial benefit whatsoever.',
      'We make no representation that DEEP Points will ever be exchangeable for money, cryptocurrency, tokens, or any other asset.',
      'We do not charge you money to earn or redeem points. No purchase, subscription, or payment is required to participate in the rewards system.',
      'You should not expect any financial return from using the App. Do not make financial decisions based on anticipated point value.',
      'Any future $DEEP token launch or conversion programme is entirely at the discretion of the company, subject to applicable law, and is not guaranteed in any way. Past point accumulation does not guarantee any future allocation or benefit.',
      'DEEP Points may be adjusted, expired, or removed at any time at our sole discretion, with or without notice.',
    ]
  },
  {
    title: 'The App is Free — How We Make Money',
    content: [
      'The core app is free to download and use. We generate revenue through Google AdMob in-app advertisements.',
      'You are never required to make a purchase or payment to access core app features.',
      'Watching rewarded video advertisements to earn bonus DEEP Points is entirely voluntary.',
      'We may introduce optional premium features in the future. Any paid features will be clearly disclosed before you are asked to make any purchase.',
    ]
  },
  {
    title: 'Background Location — Consent & Use',
    highlight: true,
    content: [
      '⚠️  This section describes our use of background location access.',
      'The App requests ACCESS_BACKGROUND_LOCATION so the step-tracker service can continue logging your walking route and distance when the app is not in the foreground.',
      'Background location is used ONLY for distance tracking during active workout sessions. It is NEVER used for advertising, profiling, or any other purpose.',
      'The service always displays a persistent notification so you are aware it is running. You can stop it at any time from the notification or from within the app.',
      'Raw GPS coordinates are processed on-device and are never stored or transmitted to our servers. Only your total session distance (in km) is saved.',
      'By granting background location permission, you consent to this use. You may revoke this permission at any time in your device Settings → Apps → DEEP Health → Permissions.',
    ]
  },
  {
    title: 'Health Data Disclaimer',
    content: [
      'Step counts, calorie estimates, and distance calculations are approximations based on your device\'s sensor data.',
      'These figures are not medically accurate and must not be used for medical, diagnostic, clinical, or therapeutic purposes.',
      'Always consult a qualified healthcare professional before starting any new fitness programme.',
      'The App is a general wellness tool only and does not constitute medical advice, diagnosis, or treatment.',
    ]
  },
  {
    title: 'Fair Use & Account Integrity',
    content: [
      'You agree not to use bots, scripts, emulators, modified APKs, or any automated or artificial methods to inflate DEEP Points.',
      'You agree not to share accounts, sell accounts, or transfer points between accounts.',
      'You agree not to attempt to reverse-engineer, decompile, tamper with, or exploit any part of the App.',
      'Accounts found to be violating this policy will be permanently suspended without notice, compensation, or right of appeal.',
      'You are solely responsible for all activity conducted under your account.',
    ]
  },
  {
    title: 'User Accounts',
    content: [
      'You must provide accurate and complete information when creating your account.',
      'You are responsible for maintaining the confidentiality of your account credentials.',
      'You must be at least 13 years old to create an account. Users aged 13–17 must have parental or guardian consent.',
      'You must not create multiple accounts to circumvent reward limits or earn additional DEEP Points.',
      'We reserve the right to suspend or terminate accounts that violate these Terms without prior notice.',
    ]
  },
  {
    title: 'Advertising',
    content: [
      'The App displays advertisements from Google AdMob. By using the App, you consent to the display of ads.',
      'Google AdMob may collect your Android Advertising ID. You can opt out of personalised ads in your device settings.',
      'We are not responsible for the content of third-party advertisements.',
      'Clicking on advertisements is voluntary. We are not responsible for any third-party websites or services reached through ads.',
    ]
  },
  {
    title: 'Intellectual Property',
    content: [
      'All content, design, code, trademarks, branding, and any other material within the App are the exclusive property of DEEP Health.',
      'You may not copy, reproduce, modify, distribute, or create derivative works from any part of the App without prior written consent.',
      'The DEEP Health name, logo, and associated marks are proprietary and may not be used without permission.',
    ]
  },
  {
    title: 'Limitation of Liability',
    content: [
      'To the maximum extent permitted by applicable law, DEEP Health shall not be liable for any indirect, incidental, special, consequential, or punitive damages.',
      'This includes but is not limited to loss of data, loss of revenue, business interruption, or any claim related to DEEP Points.',
      'Our total liability for any claim arising from use of the App is limited to zero, as the App is provided free of charge.',
      'We do not guarantee uninterrupted, error-free, or secure service. The App may be unavailable from time to time for maintenance or other reasons.',
    ]
  },
  {
    title: 'Privacy',
    content: [
      'Your use of the App is governed by our Privacy Policy, which is incorporated into these Terms by reference.',
      'Please read our Privacy Policy carefully to understand how we collect, use, and protect your data.',
      'Our Privacy Policy is available at https://deephealth.online/privacy and within the App.',
    ]
  },
  {
    title: 'Termination',
    content: [
      'We may terminate or suspend your account at any time for breach of these Terms, at our sole discretion.',
      'You may delete your account at any time via the app Settings → Delete Account.',
      'Upon termination, your right to use the App ceases immediately.',
      'Earned DEEP Points are forfeited upon account termination due to violation of these Terms and will not be reinstated.',
    ]
  },
  {
    title: 'Governing Law',
    content: [
      'These Terms shall be governed by and construed in accordance with applicable laws in the jurisdiction where DEEP Health operates.',
      'Any disputes arising from these Terms shall first be attempted to be resolved through good-faith negotiation between the parties.',
      'These Terms represent the entire agreement between you and DEEP Health regarding your use of the App and supersede any prior agreements.',
    ]
  },
  {
    title: 'Contact',
    content: [
      'If you have questions, concerns, or disputes about these Terms, please use the Report / Contact feature in the app Settings.',
      'We aim to respond to all enquiries within 30 days.',
    ]
  },
];

export default function Terms() {
  return (
    <main className="legal-page">
      <div className="legal-hero">
        <div className="legal-hero-bg" style={{ background: 'radial-gradient(ellipse 60% 80% at 50% 0%, rgba(251,191,36,0.12) 0%, transparent 70%)' }} />
        <div className="container">
          <div className="legal-label" style={{ color: '#FBBF24', background: 'rgba(251,191,36,0.10)', border: '1px solid rgba(251,191,36,0.25)' }}>
            Legal Document
          </div>
          <h1 className="legal-title">Terms &amp; <span className="gradient-text">Conditions</span></h1>
          <p className="legal-date">Last updated: 22 March 2026 — DEEP Health Tracker</p>
          <p className="legal-intro">
            These Terms &amp; Conditions govern your use of DEEP Health — Earn &amp; Track. Please read them
            carefully. By downloading, installing, or using the App, you agree to be bound by these Terms.
          </p>
        </div>
      </div>

      <div className="container legal-content">
        <div className="legal-summary">
          <h3>Key Points at a Glance</h3>
          <div className="legal-summary-grid">
            {[
              { icon: '✅', text: 'The app is completely free to use' },
              { icon: '⚠️', text: 'DEEP Points have no real monetary value' },
              { icon: '🚫', text: 'No bots, cheating, or multiple accounts' },
              { icon: '🏥', text: 'Not a medical app — consult a professional' },
              { icon: '📍', text: 'Background location only used for distance tracking' },
              { icon: '🔒', text: 'Your data is encrypted and never sold' },
            ].map((item, i) => (
              <div key={i} className="legal-summary-item">
                <span>{item.icon}</span>
                <p>{item.text}</p>
              </div>
            ))}
          </div>
        </div>

        {SECTIONS.map((section, i) => (
          <div key={i} className={`legal-section${section.highlight ? ' legal-section--highlight' : ''}`}
            style={section.highlight ? {
              background: 'rgba(251,191,36,0.04)',
              border: '1px solid rgba(251,191,36,0.18)',
              borderRadius: 16,
              padding: '32px 28px',
              marginBottom: 8,
            } : {}}>
            <div className="legal-section-num"
              style={{ color: '#FBBF24', background: 'rgba(251,191,36,0.10)', border: '1.5px solid rgba(251,191,36,0.25)' }}>
              {String(i + 1).padStart(2, '0')}
            </div>
            <div className="legal-section-body">
              <h2 className="legal-section-title">{section.title}</h2>
              <ul className="legal-list">
                {section.content.map((item, j) => (
                  <li key={j}>{item}</li>
                ))}
              </ul>
            </div>
          </div>
        ))}

        <div className="legal-footer-note">
          <p>
            By using DEEP Health, you confirm you have read and agree to these Terms and our{' '}
            <Link to="/privacy">Privacy Policy</Link>.
          </p>
        </div>
      </div>
    </main>
  );
}
